import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../../ui/dialog";
import { Button } from "../../../ui/button";
import { Input } from "../../../ui/input";
import { Label } from "../../../ui/label";
import { Textarea } from "../../../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../ui/select";
import { Switch } from "../../../ui/switch";
import { toast } from "sonner";
import type { CateringMenu } from "../../../../hooks/useCateringMenus";

interface EditMenuDialogProps {
  menu: CateringMenu | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: () => void;
}

export function EditMenuDialog({ menu, open, onOpenChange, onSave }: EditMenuDialogProps) {
  const isNew = !menu;
  const [saving, setSaving] = useState(false);

  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [description, setDescription] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [menuType, setMenuType] = useState<"buffet" | "bento" | "package">("buffet");
  const [pricePerPax, setPricePerPax] = useState("");
  const [flatPrice, setFlatPrice] = useState("");
  const [minPax, setMinPax] = useState("10");
  const [maxPax, setMaxPax] = useState("");
  const [isActive, setIsActive] = useState(true);

  useEffect(() => {
    if (menu) {
      setName(menu.name);
      setSlug(menu.slug);
      setDescription(menu.description || "");
      setImageUrl(menu.image_url || "");
      setMenuType(menu.menu_type);
      setPricePerPax(menu.price_per_pax?.toString() || "");
      setFlatPrice(menu.flat_price?.toString() || "");
      setMinPax(menu.min_pax.toString());
      setMaxPax(menu.max_pax?.toString() || "");
      setIsActive(menu.is_active);
    } else {
      setName("");
      setSlug("");
      setDescription("");
      setImageUrl("");
      setMenuType("buffet");
      setPricePerPax("");
      setFlatPrice("");
      setMinPax("10");
      setMaxPax("");
      setIsActive(true);
    }
  }, [menu, open]);

  const generateSlug = (value: string) => {
    return value.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
  };

  const handleNameChange = (value: string) => {
    setName(value);
    if (isNew) setSlug(generateSlug(value));
  };

  const handleSave = async () => {
    if (!name.trim() || !slug.trim()) {
      toast.error("Name and slug are required");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        slug: slug.trim(),
        description: description.trim() || null,
        image_url: imageUrl.trim() || null,
        menu_type: menuType,
        price_per_pax: pricePerPax ? parseFloat(pricePerPax) : null,
        flat_price: flatPrice ? parseFloat(flatPrice) : null,
        min_pax: parseInt(minPax) || 10,
        max_pax: maxPax ? parseInt(maxPax) : null,
        is_active: isActive,
      };


      onSave();
      onOpenChange(false);
    } catch (err: any) {
      toast.error(err.message || "Failed to save menu");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isNew ? "Create New Menu" : "Edit Menu"}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label>Name *</Label>
            <Input value={name} onChange={(e) => handleNameChange(e.target.value)} placeholder="e.g. Premium Buffet" />
          </div>

          <div className="space-y-2">
            <Label>Slug *</Label>
            <Input value={slug} onChange={(e) => setSlug(e.target.value)} placeholder="premium-buffet" />
          </div>

          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} />
          </div>

          <div className="space-y-2">
            <Label>Image URL</Label>
            <Input value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} placeholder="https://..." />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Menu Type</Label>
              <Select value={menuType} onValueChange={(v) => setMenuType(v as any)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="buffet">Buffet</SelectItem>
                  <SelectItem value="bento">Bento</SelectItem>
                  <SelectItem value="package">Package</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Status</Label>
              <div className="flex items-center gap-2 h-10">
                <Switch checked={isActive} onCheckedChange={setIsActive} />
                <span className="text-sm">{isActive ? "Active" : "Inactive"}</span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Price Per Pax ($)</Label>
              <Input type="number" step="0.01" value={pricePerPax} onChange={(e) => setPricePerPax(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Flat Price ($)</Label>
              <Input type="number" step="0.01" value={flatPrice} onChange={(e) => setFlatPrice(e.target.value)} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Min Pax</Label>
              <Input type="number" value={minPax} onChange={(e) => setMinPax(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Max Pax</Label>
              <Input type="number" value={maxPax} onChange={(e) => setMaxPax(e.target.value)} placeholder="No limit" />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : isNew ? "Create Menu" : "Save Changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
